import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, X, RefreshCw, Star, Trash2, Undo2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/guest-reviews")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "reviewSubmissions")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Guest Review Queue — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: GuestReviewQueue,
});

type Status = "pending" | "approved" | "rejected";

type GuestReview = {
  id: string;
  author_name: string;
  author_location: string | null;
  author_email: string | null;
  rating: number;
  title: string | null;
  body: string;
  source: string;
  status: Status;
  reviewer_notes: string | null;
  created_at: string;
};

const FILTERS: { key: Status | "all"; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function GuestReviewQueue() {
  const [rows, setRows] = useState<GuestReview[] | null>(null);
  const [filter, setFilter] = useState<Status | "all">("pending");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("guest_reviews")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);
      if (filter !== "all") query = query.eq("status", filter);
      const { data, error } = await query;
      if (error) throw error;
      setRows((data ?? []) as GuestReview[]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load reviews");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(id: string, status: Status) {
    setBusyId(id);
    try {
      const { error } = await supabase.from("guest_reviews").update({ status }).eq("id", id);
      if (error) throw error;
      toast.success(
        status === "approved"
          ? "Review published"
          : status === "rejected"
            ? "Review rejected"
            : "Review moved back to pending",
      );
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(id: string) {
    setBusyId(id);
    try {
      const { error } = await supabase.from("guest_reviews").delete().eq("id", id);
      if (error) throw error;
      toast.success("Review deleted");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl">Guest reviews</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Written reviews submitted from the website. Only approved reviews appear publicly.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
        >
          <RefreshCw size={14} aria-hidden className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={
              filter === f.key
                ? "rounded-full border border-[var(--color-ember)] bg-[var(--color-ember)] px-4 py-1.5 text-sm font-semibold text-[var(--color-ember-foreground)]"
                : "rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}
        {rows?.length === 0 && (
          <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            No reviews in this view.
          </p>
        )}
        {rows?.map((r) => (
          <article key={r.id} className="rounded-lg border border-border bg-card p-5">
            <header className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold">
                  {r.author_name}
                  {r.author_location ? (
                    <span className="text-muted-foreground font-normal"> · {r.author_location}</span>
                  ) : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()} · {r.source}
                  {r.author_email ? ` · ${r.author_email}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex gap-0.5" aria-label={`${r.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      className={
                        i < r.rating
                          ? "fill-[var(--color-ember)] text-[var(--color-ember)]"
                          : "text-muted-foreground/30"
                      }
                    />
                  ))}
                </span>
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {r.status}
                </span>
              </div>
            </header>

            {r.title && <h2 className="font-display text-lg mt-3">{r.title}</h2>}
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">
              {r.body}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {r.status !== "approved" && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => void setStatus(r.id, "approved")}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-ember)] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90 disabled:opacity-60"
                >
                  <Check size={14} aria-hidden />
                  Approve
                </button>
              )}
              {r.status !== "rejected" && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => void setStatus(r.id, "rejected")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:border-destructive hover:text-destructive disabled:opacity-60"
                >
                  <X size={14} aria-hidden />
                  Reject
                </button>
              )}
              {r.status !== "pending" && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() => void setStatus(r.id, "pending")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:opacity-60"
                >
                  <Undo2 size={14} aria-hidden />
                  Reset
                </button>
              )}
              <button
                type="button"
                disabled={busyId === r.id}
                onClick={() => void remove(r.id)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:border-destructive hover:text-destructive disabled:opacity-60"
              >
                <Trash2 size={14} aria-hidden />
                Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
